import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'
import { generateFileName } from '../../../utils/date'

export type RequestorExportType =
  | 'TIME_OFF'
  | 'VACATION_BID'
  | 'HISTORY'

export interface RequestorExportPayload {
  date: string
  timeZone: string
  timePeriod?: number
  type: RequestorExportType
  sortOrder?: 'ASC' | 'DESC'
  sortColumn?: string
}

interface ExportState {
  loading: boolean
  error: string | null
  success: boolean
}

const initialState: ExportState = {
  loading: false,
  error: null,
  success: false,
}


export const exportRequestorRequests = createAsyncThunk<
  boolean,
  RequestorExportPayload,
  {
    rejectValue: string
  }
>(
  'requestorExport/exportRequestorRequests',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post(
        endpoints.requester.requestorExport,
        payload,
        {
          responseType: 'blob',
        }
      )

      if (!(response.data instanceof Blob) || response.data.size === 0) {
        return rejectWithValue('Export returned an empty file')
      }

      const filename = generateFileName()
      const url = URL.createObjectURL(response.data)

      try {
        const link = document.createElement('a')

        link.href = url
        link.download = filename

        document.body.appendChild(link)
        link.click()
        link.remove()
      } finally {
        URL.revokeObjectURL(url)
      }

      return true
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(
          error,
          'Failed to export requests'
        )
      )
    }
  }
)

const requestorExportSlice = createSlice({
  name: 'requestorExport',
  initialState,

  reducers: {
    clearExportState: (state) => {
      state.loading = false
      state.error = null
      state.success = false
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(exportRequestorRequests.pending, (state) => {
        state.loading = true
        state.error = null
        state.success = false
      })
      .addCase(exportRequestorRequests.fulfilled, (state) => {
        state.loading = false
        state.success = true
      })
      .addCase(exportRequestorRequests.rejected, (state, action) => {
        state.loading = false
        state.success = false
        state.error = action.payload ?? 'Export failed'
      })
  },
})

export const { clearExportState } = requestorExportSlice.actions

export default requestorExportSlice.reducer